import React, { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import { gql } from '@apollo/client';

import { bookingStatus } from 'types/booking';

import { CANCEL_REFUND } from 'constants/afterSalesShortcuts';
import { useManagedMutation } from 'utils/managedHooks';
import { getRefundsURL } from 'utils/urls';
import { userShape, bookingShape, historyShape } from 'constants/propTypes';

import { showCancelThirdPartyBookingWarning } from 'utils/booking';
import ChildrenWillTravelAloneWarning from '../../AfterSalesWarning/ChildrenWillTravelAloneWarning/ChildrenWillTravelAloneWarning';
import CanPerform from 'components/shared/CanPerform';

import CannotSellByItselfWarning from '../../AfterSalesWarning/CannotSellByItselfWarning/CannotSellByItselfWarning';

import {
  getAssociatedItemRefsForSelectedItems,
  runAfterSaleValidator,
  allSegmentsCanBeCancelled,
  selectedProductsCheckedIn,
  productHasNotBeenPrrRefunded,
  ticketsWithAopInOneJourney,
  hasOppositeThroughFareWithMandatoryReturn,
  hasOppositeWithMandatoryReturnOnTemporaryOnHold,
  allSelectedItemsAreOnCarriers
} from '../AfterSalesCtas.utils';

import ForcedCancellation from './ForcedCancellation';
import IsUnconfirmedCancellation from './IsUnconfirmedCancellation';
import IsThirdPartyBookingCancellation from './IsThirdPartyBookingCancellation';

import { StyledActionButton } from './CancelSpecificBookingItemsCta.styles';
import UnselectedOppositeESThroughFareWarning from '../../AfterSalesWarning/UnselectedOppositeESThroughFareWarning/UnselectedOppositeESThroughFareWarning';
import UnselectedOppositeMandatoryReturnWarning from '../../AfterSalesWarning/UnselectedOppositeMandatoryReturnWarning/UnselectedOppositeMandatoryReturnWarning';

export const COMPONENTS = {
  forcedCancellation: 'ForcedCancellationComponent',
  isOppositeMandatoryReturn: 'UnselectedOppositeMandatoryReturnWarning',
  isBookingUnconfirmed: 'IsBookingUnconfirmedComponent',
  isBookingThirdParty: 'IsThirdPartyBookingCancellationComponent',
  default: 'DefaultComponent'
};

const onHoldOrTemporaryOnHoldBooking = [bookingStatus.TEMPORARILY_ON_HOLD, bookingStatus.ON_HOLD];

const CANCEL_PRODUCTS = gql`
  mutation CancelSpecificBookingItems(
    $bookingReference: String!
    $itemRefs: [String]!
    $overrideReason: String
    $overrideAftersalesRules: Boolean
  ) {
    cancelSpecificBookingItems(
      bookingReference: $bookingReference
      itemRefs: $itemRefs
      overrideReason: $overrideReason
      overrideAftersalesRules: $overrideAftersalesRules
    ) {
      cancellable
    }
  }
`;

const whichComponentToRender = ({ isProductsCancellable, allowAfterSalesCancel, status, booking, user, selectedItemRefs }) => {
  const isForcedCancellationRequired = !isProductsCancellable && allowAfterSalesCancel;
  const isBookingUnconfirmed = onHoldOrTemporaryOnHoldBooking.some((s) => s === status);
  const isTemporaryOnHoldWithMandatoryReturn = runAfterSaleValidator(
    booking,
    selectedItemRefs
  )([hasOppositeWithMandatoryReturnOnTemporaryOnHold]);
  const isThirdPartyBooking =
    !isForcedCancellationRequired && !isBookingUnconfirmed && showCancelThirdPartyBookingWarning({ booking, user, selectedItemRefs });

  return (
    (isForcedCancellationRequired && COMPONENTS.forcedCancellation) ||
    (isTemporaryOnHoldWithMandatoryReturn && COMPONENTS.isOppositeMandatoryReturn) ||
    (isBookingUnconfirmed && COMPONENTS.isBookingUnconfirmed) ||
    (isThirdPartyBooking && COMPONENTS.isBookingThirdParty) ||
    COMPONENTS.default
  );
};

const CancelSpecificBookingItemsCta = ({
  history: {
    push,
    location: { search }
  },
  disabled,
  selectedItemRefs,
  booking,
  user,
  willResultInChildAlone = false,
  willHaveOrphansExternalTickets = false
}) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [blockAftersalesAsCannotSellByItself, setBlockAftersalesAsCannotSellByItself] = useState(false);
  const [blockAftersalesAsChildrenWillTravelAlone, setBlockAftersalesAsChildrenWillTravelAlone] = useState(false);
  const [blockAftersalesAsOppositeUnselectedIsEsThroughFare, setBlockAftersalesAsOppositeUnselectedIsEsThroughFare] = useState(false);
  const membershipNumber = new URLSearchParams(search).get('membershipNumber');

  const [cancelSpecificBookingItems, { loading }] = useManagedMutation(CANCEL_PRODUCTS, {
    onCompleted: ({ cancelSpecificBookingItems: { cancellable } }) => {
      setIsModalVisible(false);

      if (cancellable) {
        // eslint-disable-next-line no-unused-expressions
        onHoldOrTemporaryOnHoldBooking.some((s) => s === booking.status)
          ? push(`/manage/bookings/${booking.reference}${membershipNumber ? `?membershipNumber=${membershipNumber}` : ''}`)
          : push(getRefundsURL(booking.reference, membershipNumber));
      }
    }
  });

  const isProductsCancellable = runAfterSaleValidator(booking, selectedItemRefs)([allSegmentsCanBeCancelled]);
  const isMultiJourneyWithAop = runAfterSaleValidator(booking, selectedItemRefs)([ticketsWithAopInOneJourney]);
  const isCheckedIn = runAfterSaleValidator(booking, selectedItemRefs)([selectedProductsCheckedIn('some')]);
  const isNotPrrRefunded = runAfterSaleValidator(booking, selectedItemRefs)([productHasNotBeenPrrRefunded]);
  const isOppositeThroughFareWithMandatoryReturn = runAfterSaleValidator(
    booking,
    selectedItemRefs
  )([hasOppositeThroughFareWithMandatoryReturn]);

  const isDisabled =
    disabled || !(isProductsCancellable || user.allowAfterSalesCancel) || !isMultiJourneyWithAop || isCheckedIn || !isNotPrrRefunded;

  const handleToggleModal = () => setIsModalVisible(!isModalVisible);

  const handleCancellation = (variables) => {
    if (willHaveOrphansExternalTickets) {
      return setBlockAftersalesAsCannotSellByItself(true);
    }

    if (willResultInChildAlone) {
      return setBlockAftersalesAsChildrenWillTravelAlone(true);
    }

    if (isOppositeThroughFareWithMandatoryReturn) {
      return setBlockAftersalesAsOppositeUnselectedIsEsThroughFare(true);
    }

    let extraItemRefs = [];

    const allLegs = booking.outbound.concat(booking.inbound);

    const selectedItemsCanBeCancelledOnTheirOwn = selectedItemRefs.length
      ? allSelectedItemsAreOnCarriers({ itemRefs: selectedItemRefs, segments: allLegs })
      : false;

    allLegs.forEach((leg) => {
      const passengersCancelled = [];
      leg.bookingItems.forEach((bi) => {
        if (selectedItemRefs.includes(bi.itemRef)) {
          passengersCancelled.push(bi.passengerId);
        }
      });

      const extrasCancelled = leg.extraProducts
        .filter((prod) => passengersCancelled.includes(prod.passenger.id))
        .map((prod) => prod.itemRef);

      extraItemRefs = extraItemRefs.concat(extrasCancelled);
    });

    cancelSpecificBookingItems({
      variables: {
        bookingReference: booking.reference,
        itemRefs: selectedItemsCanBeCancelledOnTheirOwn
          ? selectedItemRefs
          : getAssociatedItemRefsForSelectedItems({
              booking,
              selectedItemRefs
            }).concat(extraItemRefs),
        ...variables
      }
    });
  };

  const Component = {
    [COMPONENTS.forcedCancellation]: () => (
      <ForcedCancellation
        isDisabled={isDisabled}
        isModalVisible={isModalVisible}
        isLoading={loading}
        toggleModal={handleToggleModal}
        onSubmit={(args) => handleCancellation(args)}
        selectedItemRefs={selectedItemRefs}
        booking={booking}
        mergeThirdPartyModal={showCancelThirdPartyBookingWarning({ booking, user, selectedItemRefs })}
      />
    ),
    [COMPONENTS.isOppositeMandatoryReturn]: () => (
      <UnselectedOppositeMandatoryReturnWarning isDisabled={isDisabled} isOpen={isModalVisible} closeModal={handleToggleModal} />
    ),
    [COMPONENTS.isBookingUnconfirmed]: () => (
      <IsUnconfirmedCancellation
        isDisabled={isDisabled}
        isModalVisible={isModalVisible}
        isLoading={loading}
        toggleModal={handleToggleModal}
        onSubmit={() => handleCancellation()}
        status={booking.status}
        mergeThirdPartyModal={showCancelThirdPartyBookingWarning({ booking, user, selectedItemRefs })}
      />
    ),
    [COMPONENTS.isBookingThirdParty]: () => (
      <IsThirdPartyBookingCancellation
        isDisabled={isDisabled}
        isModalVisible={isModalVisible}
        isLoading={loading}
        toggleModal={handleToggleModal}
        onSubmit={() => handleCancellation()}
      />
    ),
    [COMPONENTS.default]: () => (
      <StyledActionButton
        type="button"
        data-cy="forced-cancellation-submit"
        data-testid="forced-cancellation-submit"
        disabled={isDisabled || loading}
        onClick={() => handleCancellation()}
      >
        <FormattedMessage id="afterSales.cancel" />
      </StyledActionButton>
    )
  };

  const componentName = whichComponentToRender({
    isProductsCancellable,
    allowAfterSalesCancel: user.allowAfterSalesCancel,
    status: booking.status,
    booking,
    user,
    selectedItemRefs
  });

  const CancellationComponent = Component[componentName];

  if (blockAftersalesAsCannotSellByItself) {
    return <CannotSellByItselfWarning isOpen={true} closeModal={() => setBlockAftersalesAsCannotSellByItself(false)} booking={booking} />;
  }

  if (blockAftersalesAsChildrenWillTravelAlone) {
    return <ChildrenWillTravelAloneWarning isOpen={true} closeModal={() => setBlockAftersalesAsChildrenWillTravelAlone(false)} />;
  }

  if (blockAftersalesAsOppositeUnselectedIsEsThroughFare) {
    return (
      <UnselectedOppositeESThroughFareWarning
        isOpen={true}
        closeModal={() => setBlockAftersalesAsOppositeUnselectedIsEsThroughFare(false)}
      />
    );
  }

  return <CanPerform afterSalesShortcut={CANCEL_REFUND}>{(isEligible) => isEligible && <CancellationComponent />}</CanPerform>;
};

CancelSpecificBookingItemsCta.propTypes = {
  disabled: PropTypes.bool.isRequired,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string).isRequired,
  booking: bookingShape,
  user: userShape,
  history: historyShape,
  willResultInChildAlone: PropTypes.bool,
  willHaveOrphansExternalTickets: PropTypes.bool
};

export default CancelSpecificBookingItemsCta;
